import { useTranslation } from 'react-i18next';

import { isPathMatches } from '~/components/app-header/utils/navigation-utils';
import { ChatInputConfig } from '~/components/chat';
import {
  EntityOrThemeWithListTypes,
  isThemeItem,
} from '~/components/search-modal/utils';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useNavigateToThemeOverview } from '~/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { queryKeys } from '~/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useDocumentSearch } from '~/views/alfred/document-search/use-document-search/useDocumentSearch';
import { useChatNavigation } from '~/views/alfred/use-chat-navigation/useChatNavigation';

interface UseChatInputHandlersProps {
  config?: ChatInputConfig;
  chatContext?: {
    documents?: string[];
    documentTypes?: string[];
    entityTags?: string[];
  };
  submitCallback?: (message?: string) => void;
  isLoading: boolean;
}

export const useChatInputHandlers = ({
  config,
  chatContext,
  submitCallback,
  isLoading,
}: UseChatInputHandlersProps) => {
  const { t } = useTranslation('chat');
  const { params, deleteQueryParam } = useQueryParams();
  const { navigateToAlfred } = useChatNavigation();
  const { trackAlfredQuerySent } = usePosthogEvents();
  const { navigateToChatMessage: navigateToAssetOverviewChatMessage } =
    useNavigateToAssetOverview();
  const { navigateToChatMessage: navigateToThemeOverviewChatMessage } =
    useNavigateToThemeOverview();
  const {
    resetStore,
    abortRequest,
    resetSession,
    clearDocumentContextMessages,
    sendMessage,
    setIsSearchWithinContext,
  } = useDocumentSearch(state => ({
    resetStore: state.resetStore,
    abortRequest: state.abortRequest,
    resetSession: state.resetSession,
    clearDocumentContextMessages: state.clearDocumentContextMessages,
    sendMessage: state.sendMessage,
    setIsSearchWithinContext: state.setIsSearchWithinContext,
  }));

  const shouldResetStore = (question: string) => {
    if (!isPathMatches(['alfred'])) {
      return true;
    }

    const restRegex = new RegExp(`^${t('resetSession')}$`, 'i');
    return restRegex.test(question);
  };

  const closeDocumentSourcesModal = () => {
    if (params[queryKeys.citationGroupId]) {
      deleteQueryParam(queryKeys.citationGroupId);
    }
  };

  const submitHandler = (
    inputValue: string,
    onSubmitCallbackHandler: () => void
  ) => {
    if (isLoading || !inputValue.trim()) {
      return;
    }

    const question = inputValue.trim();

    if (config?.clearDocumentContextOnSubmit) {
      resetSession(true);
      clearDocumentContextMessages();
    }

    // reset store if asking question not from alfred
    // in other words, init new session
    if (shouldResetStore(question)) {
      resetStore();
    }

    sendMessage({
      question,
      ...(chatContext && {
        ancillary: {
          documents: chatContext?.documents,
          document_types: chatContext?.documentTypes,
          entity_tags: chatContext?.entityTags,
        },
      }),
    });

    if (isPathMatches(['alfred'])) {
      trackAlfredQuerySent(POSTHOG_TRACKING_SOURCES.ALFRED, 'Document');
    }

    onSubmitCallbackHandler();
    setIsSearchWithinContext(true);
    closeDocumentSourcesModal();
    submitCallback?.(question);
    navigateToAlfred('document');
  };

  const onSelect = (item: EntityOrThemeWithListTypes, clear: () => void) => {
    const isTheme = isThemeItem(item);

    if (isTheme) {
      navigateToThemeOverviewChatMessage(item.id);
    } else {
      navigateToAssetOverviewChatMessage(item.tag);
    }

    clear();
  };

  const onKeyDown = (
    _: React.KeyboardEvent<HTMLElement>,
    inputText: string,
    onSubmitCallbackHandler: () => void
  ) => {
    submitHandler(inputText, onSubmitCallbackHandler);
  };

  const onSubmit = (
    event:
      | React.FormEvent<HTMLFormElement>
      | React.KeyboardEvent<HTMLTextAreaElement>,
    inputText: string,
    onSubmitCallbackHandler: () => void
  ) => {
    event.preventDefault();
    submitHandler(inputText, onSubmitCallbackHandler);
  };

  return {
    onSubmit,
    onKeyDown,
    onSelect,
    onAbort: abortRequest,
  };
};
